// src/components/Dashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { today, PRESETS, formatNY } from '../utils/dateRange';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(today);
  const [showFilters, setShowFilters] = useState(false);

  const applyPreset = (range) => setDateRange(range);

  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchDashboardData, 400);
    return () => clearTimeout(debounceRef.current);
  }, [dateRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/reports/dashboard', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No data available for the selected date range.</p>
        <p className="text-sm text-gray-400 mt-1">Try expanding the date range or check that campaigns have recorded activity.</p>
      </div>
    );
  }

  const { summary, top_campaigns, recent_activity } = stats;
  const todayStr = today().start;
  const hasActiveFilters = dateRange.start !== todayStr || dateRange.end !== todayStr;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <button
          type="button"
          onClick={() => setShowFilters(v => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100 shadow-sm"
          aria-expanded={showFilters}
          aria-controls="filter-panel"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {hasActiveFilters && (
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full" aria-label="Filters applied" />
          )}
        </button>
      </div>

      {showFilters && (
        <div id="filter-panel" className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex flex-wrap gap-2 mb-3">
            {PRESETS.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p.get())}
                className="px-3 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="date"
              aria-label="Start date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <input
              type="date"
              aria-label="End date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <StatCard
          title="Total Clicks"
          value={summary.total_clicks || 0}
          icon="clicks"
          color="blue"
        />
        <StatCard
          title="Conversions"
          value={summary.total_conversions || 0}
          icon="conversions"
          color="green"
        />
        <StatCard
          title="Revenue"
          value={`$${parseFloat(summary.total_revenue || 0).toFixed(2)}`}
          icon="revenue"
          color="purple"
        />
        <StatCard
          title="Profit"
          value={`$${parseFloat(summary.total_profit || 0).toFixed(2)}`}
          icon="profit"
          color="indigo"
        />
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Key Metrics</h3>
          <div className="space-y-4">
            <MetricRow
              label="Conversion Rate"
              value={`${parseFloat(summary.avg_conversion_rate || 0).toFixed(2)}%`}
            />
            <MetricRow
              label="Total Cost"
              value={`$${parseFloat(summary.total_cost || 0).toFixed(2)}`}
            />
            <MetricRow
              label="ROI"
              value={`${summary.total_cost > 0 ? ((summary.total_profit / summary.total_cost) * 100).toFixed(2) : 0}%`}
            />
          </div>
        </div>

        {/* Top Campaigns */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Top Campaigns by Profit</h3>
          <div className="space-y-3">
            {top_campaigns.map((campaign, index) => (
              <div key={campaign.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <div>
                  <div className="font-medium">{index + 1}. {campaign.name}</div>
                  <div className="text-sm text-gray-500">
                    {campaign.clicks} clicks • {campaign.conversions} conversions
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-bold ${parseFloat(campaign.profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${parseFloat(campaign.profit || 0).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Recent Conversions</h3>

        {/* Mobile: stacked rows */}
        <ul className="sm:hidden divide-y divide-gray-200 -mx-4">
          {recent_activity.map((activity) => (
            <li key={activity.id} className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{activity.campaign_name}</div>
                <div className="text-xs text-gray-500">{formatNY(activity.created_at)}</div>
              </div>
              <div className="shrink-0 text-sm font-semibold text-green-600">
                ${parseFloat(activity.revenue).toFixed(2)}
              </div>
            </li>
          ))}
        </ul>

        {/* Desktop: existing table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recent_activity.map((activity) => (
                <tr key={activity.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{activity.campaign_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                    ${parseFloat(activity.revenue).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatNY(activity.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const STAT_ICONS = {
  clicks: (
    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
    </svg>
  ),
  conversions: (
    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  revenue: (
    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  profit: (
    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
};

function StatCard({ title, value, icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    indigo: 'bg-indigo-500'
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`${colorClasses[color]} p-2 sm:p-3 rounded-full`}>
          {STAT_ICONS[icon]}
        </div>
      </div>
    </div>
  );
}

function MetricRow({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-gray-600">{label}</span>
      <span className="font-semibold text-gray-900">{value}</span>
    </div>
  );
}

export default Dashboard;